import type { LinkHTMLAttributes, ReactNode } from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type { AppState } from 'index';

import { MARKET_PLACES } from 'constants/marketPlaces';

import { setSeoLinks } from '@Modules/seo/slice';
import { selectRegionTitleFromApp, selectRegionWhereTitleFromApp } from '@Modules/app/selectors';

import { OgLocale } from './constants';
import { HreflangMeta } from './HreflangMeta';
import { MetaCanonical } from './MetaCanonical';
import { MetaDescription } from './MetaDescription';
import { MetaImage } from './MetaImage';
import { MetaTitle } from './MetaTitle';

type Props = {
  canonical?: string;
  skipCanonical?: boolean;
  robots?: string | null;
  yandex?: string | null;
  description?: string;
  image?: string;
  title?: string;
  children?: ReactNode;
  ogType?: string;
  links?: Array<LinkHTMLAttributes<HTMLLinkElement>>;
};

export const Meta = ({
  canonical,
  skipCanonical = false,
  robots = 'index, follow',
  yandex,
  description,
  image,
  title,
  children,
  ogType,
  links,
}: Props) => {
  const geoTitle = useSelector(selectRegionTitleFromApp);
  const whereTitle = useSelector(selectRegionWhereTitleFromApp);
  const lang = useSelector((state: AppState) => state.app.language);
  const seo = useSelector((state: AppState) => state.seo);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const defaultTitle =
    title || `${t('meta.bulletinBoard')}${geoTitle ? ` - ${geoTitle}` : ''} - BirBir`;
  const defaultDescription =
    description ||
    `${t('meta.announcements')}${whereTitle ? ` ${whereTitle}` : ''}. ${
      geoTitle ? `${geoTitle}: ` : ''
    }${t('meta.postAd')}`;

  const robotsFromApi = [seo.meta?.noIndex ? 'noindex' : '', seo.meta?.noFollow ? 'nofollow' : '']
    .filter(Boolean)
    .join(', ');

  useEffect(() => {
    dispatch(setSeoLinks(links || seo.links));
  }, [dispatch, links, seo.links]);

  return (
    <>
      {!skipCanonical && <MetaCanonical canonical={canonical} />}
      <MetaImage image={image || seo.image} />
      <MetaTitle title={seo.meta?.title || defaultTitle} />
      <MetaDescription description={seo.meta?.metaDescription || defaultDescription} />
      {/* HreflangMeta handles the hreflang tags separately to avoid duplication issues with React Helmet */}
      <HreflangMeta />
      <Helmet htmlAttributes={{ lang }}>
        <meta property="og:site_name" content={`BirBir - ${t('meta.bulletinBoardInUzbekistan')}`} />
        <meta property="og:locale" content={OgLocale[lang]} />
        <meta property="og:type" content={ogType || seo.ogType || 'website'} />
        {seo.meta?.metaKeywords && <meta name="keywords" content={seo.meta.metaKeywords} />}
        {robotsFromApi && <meta name="robots" content={robotsFromApi} />}
        {robots && !robotsFromApi && <meta name="robots" content={robots} />}
        {yandex && <meta name="yandex" content={yandex} />}

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'WebSite',
            name: 'BirBir',
            url: 'https://birbir.uz',
            inLanguage: lang,
            description: t('meta.websiteSchema'),
            potentialAction: {
              '@type': 'SearchAction',
              target: `https://birbir.uz/${lang}/all/search?search={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Organization',
            name: 'BirBir',
            url: 'https://birbir.uz/ru/all',
            logo: 'https://birbir.uz/static/logo.svg',
            alternateName: [
              'Bir Bir',
              'Быр Быр',
              'Бырбыр',
              'Бир бир',
              'БирБир',
              'bir bir uz',
              'birbir uz',
              'bir-bir',
            ],
            sameAs: [
              'https://www.instagram.com/birbir.uz',
              'https://t.me/birbir_uz',
              'https://www.linkedin.com/company/birbir/',
              'https://www.facebook.com/profile.php?id=61565648775523',
            ],
            contactPoint: [
              {
                '@type': 'ContactPoint',
                url: 'https://t.me/BirBir_Support',
                contactType: 'customer support',
                areaServed: 'UZ',
                availableLanguage: ['Uzbek', 'Russian'],
              },
            ],
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Minor, 70, Tashkent, Uzbekistan',
              addressLocality: 'Toshkent',
              addressRegion: 'Toshkent',
              postalCode: '100017',
              addressCountry: 'UZ',
            },
            employee: [
              {
                '@type': 'Person',
                name: 'Andrey Teryoshin',
                jobTitle: 'CEO',
                sameAs: 'https://www.linkedin.com/in/andrey-teryoshin-36962929/',
              },
              {
                '@type': 'Person',
                name: 'Alexander Pekin',
                jobTitle: 'CPO',
                sameAs: 'https://www.linkedin.com/in/alexander-pekin/',
              },
              {
                '@type': 'Person',
                name: 'Alexander Bayzarov',
                jobTitle: 'COO',
                sameAs: 'https://www.linkedin.com/in/bayzarov/',
              },
            ],
            founder: {
              '@type': 'Person',
              name: 'Alexander Pekin',
            },
            foundingDate: '2024-04-01',
            foundingLocation: {
              '@type': 'Place',
              name: '[Uzbekistan]',
            },
            knowsAbout: 'Technology, Software, Development',
            makesOffer: {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Classified Platform',
              },
              priceCurrency: 'UZS',
              price: '0',
            },
            brand: {
              '@type': 'Brand',
              name: 'BirBir',
              logo: 'https://birbir.uz/static/logo.svg',
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'SoftwareApplication',
            name: 'Birbir',
            description: t('appDownload.schemaDescription'),
            operatingSystem: 'Android, iOS',
            applicationCategory: 'Shopping',
            downloadUrl: [MARKET_PLACES.googlePlaySchema, MARKET_PLACES.appStoreSchema],
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'UZS',
            },
          })}
        </script>
        {children}
      </Helmet>
    </>
  );
};

Meta.Image = MetaImage;
Meta.Title = MetaTitle;
Meta.Description = MetaDescription;
Meta.Canonical = MetaCanonical;
